import { useEffect, useState } from 'react'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { SubmitButton } from '@journ/ui/submit-button'
import { Textarea } from '@journ/ui/textarea'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import type { SpawnMode, UpdateWorkItemInput } from '../domain/workflow'
import { editingWorkItemId$, workItems$ } from '../store'
import { useUpdateWorkItem } from '../hooks/use-update-work-item'
import { useUpdateSpawnMode } from '../hooks/use-update-spawn-mode'
import { useReassignWorkflow } from '../hooks/use-reassign-workflow'
import { useAllWorkflows } from '../hooks/use-all-workflows'

const WorkItemEditSheet = () => {
	const editingId = useSelector(editingWorkItemId$)
	const workItems = useSelector(workItems$)
	const c = useIntlayer('work-item').edit

	const item = editingId ? (workItems.find(w => w.id === editingId) ?? null) : null
	const spawns = item ? workItems.filter(w => w.parentId === item.id) : []

	const { update } = useUpdateWorkItem()
	const { update: updateMode } = useUpdateSpawnMode()
	const { reassign } = useReassignWorkflow()
	const { workflows: allWorkflows } = useAllWorkflows()

	const [title, setTitle] = useState('')
	const [externalId, setExternalId] = useState('')
	const [notes, setNotes] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [reassignWorkflowId, setReassignWorkflowId] = useState('')
	const [reassignPhaseId, setReassignPhaseId] = useState('')
	const [reassigning, setReassigning] = useState(false)

	useEffect(() => {
		if (item) {
			setTitle(item.title)
			setExternalId(item.externalId ?? '')
			setNotes(item.notes ?? '')
			setReassignWorkflowId('')
			setReassignPhaseId('')
		}
	}, [item])

	const reassignTargetWorkflow = allWorkflows.find(w => w.id === reassignWorkflowId)
	const reassignPhases = reassignTargetWorkflow
		? [...reassignTargetWorkflow.phases].sort((a, b) => a.order - b.order)
		: []
	const reassignPhase = reassignPhases.find(p => p.id === reassignPhaseId)
	const reassignInboxStep =
		reassignPhase?.teamWorkflow.steps.find(s => s.isInbox) ?? reassignPhase?.teamWorkflow.steps[0]
	const canReassign = Boolean(item && !item.parentId && reassignTargetWorkflow && reassignPhase && reassignInboxStep)

	const doReassign = async () => {
		if (!item || !reassignTargetWorkflow || !reassignPhase || !reassignInboxStep) return
		setReassigning(true)
		const updated = await reassign({
			id: item.id,
			companyWorkflowId: reassignTargetWorkflow.id,
			phaseId: reassignPhase.id,
			firstStepId: reassignInboxStep.id,
		})
		setReassigning(false)
		if (updated) editingWorkItemId$.set(null)
	}

	const close = () => editingWorkItemId$.set(null)

	const canSubmit = Boolean(title.trim())

	const submit = async () => {
		if (!item || !canSubmit) return

		const patch: UpdateWorkItemInput = { id: item.id }
		const trimmedTitle = title.trim()
		const nextExternalId = externalId.trim() || null
		const nextNotes = notes.trim() || null

		if (trimmedTitle !== item.title) patch.title = trimmedTitle
		if (nextExternalId !== item.externalId) patch.externalId = nextExternalId
		if (nextNotes !== item.notes) patch.notes = nextNotes

		setSubmitting(true)
		const updated = await update(patch)
		setSubmitting(false)
		if (updated) close()
	}

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		await submit()
	}

	const selectMode = (spawnId: string, current: SpawnMode | null, mode: SpawnMode) => {
		if (current === mode) return
		updateMode(spawnId, mode)
	}

	return (
		<Sheet open={item !== null} onOpenChange={close}>
			<SheetContent stack title={c.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{c.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<ScrollArea className='h-full p-0 pb-28'>
					<form onSubmit={handleSubmit} className='flex flex-col gap-5'>
						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.titleLabel}</Label>
							<Input
								value={title}
								onChange={e => setTitle(e.target.value)}
								placeholder={c.titlePlaceholder.value}
								autoFocus
								autoComplete='off'
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.externalIdLabel}</Label>
							<Input
								value={externalId}
								onChange={e => setExternalId(e.target.value)}
								placeholder={c.externalIdPlaceholder.value}
								autoComplete='off'
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.notesLabel}</Label>
							<Textarea
								value={notes}
								onChange={e => setNotes(e.target.value)}
								placeholder={c.notesPlaceholder.value}
								rows={3}
								className='resize-none'
							/>
						</div>

						{spawns.length > 0 && (
							<div className='space-y-3'>
								<Label className='text-xs text-878787 font-normal'>{c.spawnsLabel}</Label>
								<div className='flex flex-col gap-3'>
									{spawns.map(st => (
										<div key={st.id} className='space-y-1.5'>
											<span className='text-sm block truncate'>{st.title}</span>
											<div className='flex gap-2'>
												{(['blocking', 'async'] as SpawnMode[]).map(mode => (
													<button
														key={mode}
														type='button'
														onClick={() => selectMode(st.id, st.spawnMode, mode)}
														className={`flex-1 border px-3 py-2 text-xs transition-colors ${
															st.spawnMode === mode
																? 'border-foreground bg-foreground text-background'
																: 'border-border bg-background text-foreground hover:bg-muted/40'
														}`}
													>
														{mode === 'blocking' ? c.modeBlocking : c.modeAsync}
													</button>
												))}
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{item && !item.parentId && (
							<div className='space-y-3 border-t border-border pt-5'>
								<Label className='text-xs text-878787 font-normal'>{c.reassignLabel}</Label>
								<p className='text-xs text-muted-foreground'>{c.reassignHint}</p>
								<Select
									value={reassignWorkflowId}
									onValueChange={v => {
										setReassignWorkflowId(v)
										setReassignPhaseId('')
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder={c.reassignWorkflowPlaceholder.value} />
									</SelectTrigger>
									<SelectContent>
										{allWorkflows
											.filter(w => w.id !== item.companyWorkflowId)
											.map(w => (
												<SelectItem key={w.id} value={w.id}>
													{w.name}
												</SelectItem>
											))}
									</SelectContent>
								</Select>

								{reassignTargetWorkflow && (
									<Select value={reassignPhaseId} onValueChange={setReassignPhaseId}>
										<SelectTrigger>
											<SelectValue placeholder={c.reassignPhasePlaceholder.value} />
										</SelectTrigger>
										<SelectContent>
											{reassignPhases.map(p => (
												<SelectItem key={p.id} value={p.id}>
													{p.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}

								<Button
									type='button'
									variant='outline'
									size='sm'
									disabled={!canReassign || reassigning}
									onClick={doReassign}
								>
									{c.reassignButton}
								</Button>
							</div>
						)}
					</form>
				</ScrollArea>

				<div className='absolute bottom-0 left-0 right-0 p-4 bg-background border-t border-border'>
					<div className='flex justify-end space-x-4'>
						<Button variant='outline' type='button' onClick={close}>
							{c.cancel}
						</Button>
						<SubmitButton type='button' isSubmitting={submitting} disabled={!canSubmit} onClick={submit}>
							{c.save}
						</SubmitButton>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}

export { WorkItemEditSheet }
